import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  schema?: object;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  schema,
  articleData,
}: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    const fullTitle = `${title} - 猛男诞生记`;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl || `https://mengnan-danshengji.com${location.pathname}`, true);
    setMeta('og:site_name', '猛男诞生记', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    canonicals.forEach((el) => el.remove());
    if (canonicalUrl) {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    if (articleData) {
      if (articleData.publishedTime) setMeta('article:published_time', articleData.publishedTime, true);
      if (articleData.modifiedTime) setMeta('article:modified_time', articleData.modifiedTime, true);
      if (articleData.author) setMeta('article:author', articleData.author, true);
      if (articleData.tags) {
        articleData.tags.forEach((tag) => {
          const el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', tag);
          document.head.appendChild(el);
        });
      }
    }
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, articleData, location.pathname]);

  useEffect(() => {
    if (!schema) return;
    const existing = document.querySelector('script[type="application/ld+json"][data-seo]');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'true');
    script.textContent = JSON.stringify(schema, null, 2);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[type="application/ld+json"][data-seo]');
      if (el) el.remove();
    };
  }, [schema]);

  return null;
}